import { Link } from "react-router-dom";
import { useScrollReveal } from "../../../hooks/useScrollReveal";

const industries = [
  {
    title: "Professional Services",
    desc: "Law firms, consulting agencies, accountants—where client management and follow-up are mission critical.",
    tag: "Client Operations",
    img: "https://public.readdy.ai/ai/img_res/db5fbb20525bdb162a22af50eb2c6af2.jpg",
  },
  {
    title: "Contractor & Home Services",
    desc: "HVAC, plumbing, roofing, and field services that need seamless scheduling and job management without gaps.",
    tag: "Field Operations",
    img: "https://readdy.ai/api/search-image?query=professional%20contractor%20or%20tradesperson%20silhouette%20working%20at%20dusk%20with%20dramatic%20orange%20and%20dark%20sky%20background%2C%20industrial%20tools%20and%20equipment%2C%20moody%20cinematic%20photography%2C%20dark%20atmospheric%20construction%20scene%20with%20powerful%20contrast%20and%20depth&width=800&height=1000&seq=ind_apple_02&orientation=portrait",
  },
  {
    title: "Real Estate & Brokerage",
    desc: "Brokerages and agents who need structured lead pipelines and consistent client communication systems.",
    tag: "Pipeline Management",
    img: "https://readdy.ai/api/search-image?query=dramatic%20luxury%20residential%20architecture%20at%20twilight%2C%20dark%20moody%20real%20estate%20photography%2C%20sleek%20contemporary%20home%20exterior%20with%20warm%20interior%20lights%20glowing%20against%20dark%20evening%20sky%2C%20premium%20property%20aesthetic%20with%20cinematic%20depth%20and%20atmosphere&width=800&height=1000&seq=ind_apple_03&orientation=portrait",
  },
];

export default function IndustriesSection() {
  const { ref: headRef, visible: headVisible } = useScrollReveal(0.1);

  return (
    <section className="w-full bg-[#050507] py-36 px-6">
      {/* Header */}
      <div
        ref={headRef as React.RefObject<HTMLDivElement>}
        className="max-w-7xl mx-auto text-center mb-20"
        style={{
          opacity: headVisible ? 1 : 0,
          transform: headVisible ? "translateY(0)" : "translateY(30px)",
          transition: "opacity 0.9s ease, transform 0.9s ease",
        }}
      >
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#4ADE80]/80 mb-5 block">
          Industries
        </span>
        <h2
          className="font-extrabold text-white leading-[0.95] tracking-[-0.04em] max-w-3xl mx-auto"
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: "clamp(2.8rem, 5vw, 5rem)",
          }}
        >
          Built for Operationally Demanding Businesses
        </h2>
      </div>

      {/* Tall image cards */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-5">
        {industries.map((ind, i) => (
          <IndustryCard key={ind.title} ind={ind} delay={i * 0.15} />
        ))}
      </div>

      {/* CTA */}
      <div className="max-w-7xl mx-auto text-center mt-16">
        <Link
          to="/industries"
          className="whitespace-nowrap inline-flex items-center gap-3 px-8 py-4 rounded-full border border-white/15 text-white/70 text-sm font-medium hover:border-[#4ADE80]/40 hover:text-white transition-all duration-200 cursor-pointer"
        >
          View Industry Solutions <i className="ri-arrow-right-line" />
        </Link>
      </div>
    </section>
  );
}

function IndustryCard({
  ind,
  delay,
}: {
  ind: { title: string; desc: string; tag: string; img: string };
  delay: number;
}) {
  const { ref, visible } = useScrollReveal(0.08);

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className="group relative rounded-3xl overflow-hidden cursor-pointer"
      style={{
        height: "540px",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(50px)",
        transition: `opacity 1s ease ${delay}s, transform 1s ease ${delay}s`,
      }}
    >
      {/* Background image */}
      <img
        src={ind.img}
        alt={ind.title}
        className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700 ease-out"
      />

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#050507] via-[#050507]/50 to-transparent" />

      {/* Content at bottom */}
      <div className="absolute bottom-0 left-0 right-0 p-8">
        <span className="inline-block text-xs font-semibold uppercase tracking-[0.15em] text-[#4ADE80] mb-3 bg-[#4ADE80]/10 border border-[#4ADE80]/20 rounded-full px-3 py-1">
          {ind.tag}
        </span>
        <h3
          className="text-2xl font-bold text-white tracking-[-0.02em] mb-3"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          {ind.title}
        </h3>
        <p className="text-sm text-white/50 leading-relaxed">{ind.desc}</p>
      </div>
    </div>
  );
}
